import { useEffect, useState, useRef } from 'react';
import AdminQuickNav from '../../components/AdminQuickNav';
import { aiDirectivesService, type EditorialDirective, type EditorialLog, type AgentJob } from '../../services/aiDirectivesService';
import { HiSparkles, HiTrash, HiInformationCircle, HiPlus, HiLightningBolt, HiCheckCircle, HiExternalLink } from 'react-icons/hi';
import { Link } from 'react-router-dom';

export default function AIAgentsAdminPage() {
  const [directives, setDirectives] = useState<EditorialDirective[]>([]);
  const [logs, setLogs] = useState<EditorialLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [newInstruction, setNewInstruction] = useState('');
  const [priority, setPriority] = useState(1);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // وضعیت تولید دستی با هوش مصنوعی
  const [generating, setGenerating] = useState(false);
  const [activeJob, setActiveJob] = useState<AgentJob | null>(null);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dirs, editorialLogs] = await Promise.all([
        aiDirectivesService.getDirectives().catch(() => []),
        aiDirectivesService.getEditorialLogs().catch(() => []),
      ]);
      setDirectives(dirs);
      setLogs(editorialLogs);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  // هندلر کلیک دکمه تولید فوری مقاله (جایگزین npm run generate-now)
  const handleTriggerGeneration = async () => {
    if (generating) return;
    setGenerating(true);
    setActiveJob(null);
    setMsg(null);

    try {
      const job = await aiDirectivesService.triggerManualGeneration();
      setActiveJob(job);

      // استعلام دوره‌ای هر ۳ ثانیه برای دریافت وضعیت نهایی کار
      let pollCount = 0;
      const maxPolls = 40; // حداکثر ۲ دقیقه

      pollTimerRef.current = setInterval(async () => {
        pollCount++;
        try {
          const updated = await aiDirectivesService.getJobStatus(job.id);
          setActiveJob(updated);

          if (updated.status === 'completed') {
            clearInterval(pollTimerRef.current!);
            setGenerating(false);
            await loadData();
          } else if (updated.status === 'failed') {
            clearInterval(pollTimerRef.current!);
            setGenerating(false);
            setMsg({
              type: 'error',
              text: `تولید مقاله با خطا مواجه شد: ${updated.error_message || 'عدم تایید سردبیر یا خطای شبکه'}`,
            });
          } else if (pollCount >= maxPolls) {
            clearInterval(pollTimerRef.current!);
            setGenerating(false);
            setMsg({
              type: 'error',
              text: 'پاسخی از سرور دریافت نشد. مطمئن شوید سرور ایجنت‌ها در ترمینال با دستور npm start روشن است.',
            });
          }
        } catch (err) {
          console.error('Polling error', err);
        }
      }, 3000);
    } catch (err: any) {
      setGenerating(false);
      setMsg({ type: 'error', text: err.message || 'خطا در ارسال درخواست تولید به سرور.' });
    }
  };

  const handleAddDirective = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newInstruction.trim()) return;
    setSaving(true);
    setMsg(null);
    try {
      await aiDirectivesService.addDirective(newInstruction.trim(), priority);
      setNewInstruction('');
      setPriority(1);
      setMsg({ type: 'success', text: 'دستور جدید با موفقیت اضافه شد و در چرخه بعدی تولید مقاله اعمال می‌شود.' });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'خطا در ثبت دستور جدید.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: string, current: boolean) => {
    try {
      await aiDirectivesService.toggleDirective(id, !current);
      setDirectives((prev) =>
        prev.map((d) => (d.id === id ? { ...d, is_active: !current } : d))
      );
    } catch (err: any) {
      alert('خطا در تغییر وضعیت: ' + err.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('آیا از حذف این دستور اطمینان دارید؟')) return;
    try {
      await aiDirectivesService.deleteDirective(id);
      setDirectives((prev) => prev.filter((d) => d.id !== id));
    } catch (err: any) {
      alert('خطا در حذف دستور: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <AdminQuickNav />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/20 rounded-2xl text-primary">
              <HiSparkles className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white">
                مدیریت ایجنت‌های هوش مصنوعی (AI Multi-Agent System)
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                کنترل دستورات تحریریه روزنامه‌نگار، رصد سردبیر و تولید آنی محتوا
              </p>
            </div>
          </div>
        </div>

        {/* کارت اکشن سریع: تولید و انتشار فوری مقاله (Trigger Manual Run) */}
        <div className="relative overflow-hidden bg-gradient-to-r from-amber-500/10 via-primary/10 to-orange-500/10 border-2 border-primary/30 rounded-3xl p-6 md:p-8 mb-8 shadow-2xl">
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-primary font-bold text-sm mb-2">
                <HiLightningBolt className="w-5 h-5 animate-pulse" />
                اجرای آنی ایجنت‌ها بدون معطلی (Manual Trigger)
              </div>
              <h2 className="text-xl md:text-2xl font-black text-white mb-2">
                تولید و انتشار همین الان یک مقاله تخصصی گیمینگ
              </h2>
              <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
                با کلیک روی این دکمه، ایجنت خبرنگار فوراً ترندهای چند ساعت اخیر را جستجو کرده، مقاله سئوشده را می‌نویسد، کاور باکیفیت طراحی می‌کند و پس از تایید سردبیر، مستقیماً در بلاگ منتشر می‌نماید.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              <button
                onClick={handleTriggerGeneration}
                disabled={generating}
                className="w-full md:w-auto flex items-center justify-center gap-3 bg-primary hover:bg-primary/90 text-black font-extrabold px-6 py-4 rounded-2xl shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:hover:scale-100"
              >
                {generating ? (
                  <>
                    <span className="w-5 h-5 border-3 border-black border-t-transparent rounded-full animate-spin"></span>
                    <span>ایجنت‌ها در حال کار هستند...</span>
                  </>
                ) : (
                  <>
                    <HiSparkles className="w-6 h-6 text-black" />
                    <span className="text-base whitespace-nowrap">🚀 تولید و انتشار فوری مقاله</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* پیام وضعیت اجرای جاب دستی */}
          {generating && (
            <div className="mt-5 p-4 rounded-2xl bg-black/60 border border-primary/30 flex items-center gap-4 animate-pulse">
              <div className="w-4 h-4 rounded-full bg-primary animate-ping"></div>
              <div className="text-xs md:text-sm text-gray-200">
                <span className="font-bold text-primary">وضعیت: </span>
                {activeJob?.status === 'running'
                  ? 'خبرنگار در حال نگارش متن و سردبیر در حال ارزیابی کیفی است (معمولاً ۳۰ تا ۶۰ ثانیه زمان می‌برد)...'
                  : 'درخواست در صف قرار گرفت؛ سرور ایجنت‌ها در حال آماده‌سازی است...'}
              </div>
            </div>
          )}

          {activeJob?.status === 'completed' && activeJob.result_post_slug && (
            <div className="mt-5 p-4 rounded-2xl bg-green-500/20 border border-green-500/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-green-300">
              <div className="flex items-center gap-2 text-sm font-bold">
                <HiCheckCircle className="w-6 h-6 text-green-400 shrink-0" />
                <span>🎉 تبریک! مقاله جدید با موفقیت تولید و منتشر شد: «{activeJob.result_post_title || 'مشاهده مقاله'}»</span>
              </div>
              <Link
                to={`/blog/${activeJob.result_post_slug}`}
                target="_blank"
                className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-black font-bold px-4 py-2 rounded-xl text-xs transition-colors shrink-0"
              >
                <span>مشاهده مقاله در سایت</span>
                <HiExternalLink className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>

        {/* کارت معرفی ۳ ایجنت فعال */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-blue-500/20 text-blue-400">ایجنت ۱</span>
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
            </div>
            <h3 className="font-bold text-lg text-white mb-1">The AI Journalist</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              تحلیل ترندهای استیم و پی‌سی با Google Search Grounding، نگارش مقالات سئوشده و تولید تصاویر با Imagen 3.
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-purple-500/20 text-purple-400">ایجنت ۲</span>
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
            </div>
            <h3 className="font-bold text-lg text-white mb-1">The Editor-in-Chief</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              کنترل کیفیت و ممیزی سخت‌گیرانه مقالات پیش از انتشار؛ در صورت کسب نمره بالای ۸۰ مجوز انتشار خودکار صادر می‌شود.
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-400">ایجنت ۳</span>
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
            </div>
            <h3 className="font-bold text-lg text-white mb-1">The Community Moderator</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              پایش بلادرنگ نظرات کاربران، فیلتر اسپم و فحاشی و پاسخگویی خودکار با هویت رسمی ربات دستیار اکتیو لجند.
            </p>
          </div>
        </div>

        {/* فرم ثبت دستور جدید تحریریه */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
          <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
            <HiPlus className="text-primary" />
            تعیین جهت‌گیری و دستور جدید برای ایجنت روزنامه‌نگار
          </h2>
          <p className="text-xs text-gray-400 mb-4">
            هر دستوری که اینجا بنویسید مستقیماً در مغز تصمیم‌گیری ایجنت اعمال شده و موضوع مقالات آینده را طبق خواسته شما شکل می‌دهد.
          </p>

          {msg && (
            <div className={`p-3 rounded-xl mb-4 text-sm ${msg.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
              {msg.text}
            </div>
          )}

          <form onSubmit={handleAddDirective} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">متن دستور به ایجنت</label>
              <textarea
                value={newInstruction}
                onChange={(e) => setNewInstruction(e.target.value)}
                placeholder="مثلاً: از این به بعد بیشتر روی اخبار بازی‌های مستقل (Indie)، تخفیف‌های ویژه استیم و آپدیت‌های بازی‌های شوتر تمرکز کن."
                rows={3}
                className="w-full bg-black/60 border border-white/10 rounded-xl p-3 text-sm text-white focus:outline-none focus:border-primary"
                required
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-300">اولویت دستور:</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="bg-black/60 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white"
                >
                  <option value={1}>عادی (۱)</option>
                  <option value={2}>مهم (۲)</option>
                  <option value={3}>بسیار حیاتی (۳)</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={saving || !newInstruction.trim()}
                className="btn-primary text-sm px-6 py-2.5 rounded-xl disabled:opacity-50"
              >
                {saving ? 'در حال ثبت...' : 'ثبت دستور در مغز ایجنت'}
              </button>
            </div>
          </form>
        </div>

        {/* لیست دستورات فعال تحریریه */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
          <h2 className="text-lg font-bold text-white mb-4">دستورات فعال در پایگاه‌داده تحریریه</h2>
          {loading ? (
            <p className="text-sm text-gray-400">در حال بارگذاری دستورات...</p>
          ) : directives.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm">
              <HiInformationCircle className="w-8 h-8 mx-auto mb-2 text-gray-500" />
              هنوز دستوری ثبت نشده است. ایجنت طبق تنظیمات عمومی ترندهای گیمینگ عمل می‌کند.
            </div>
          ) : (
            <div className="space-y-3">
              {directives.map((item) => (
                <div
                  key={item.id}
                  className={`p-4 rounded-xl border flex items-center justify-between gap-4 transition-colors ${
                    item.is_active ? 'bg-black/40 border-white/10' : 'bg-black/20 border-white/5 opacity-60'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold ${
                        item.priority >= 3 ? 'bg-red-500/20 text-red-400' : item.priority === 2 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        اولویت {item.priority}
                      </span>
                      <span className="text-[11px] text-gray-500">
                        {new Date(item.created_at).toLocaleDateString('fa-IR')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-200">{item.instruction}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggle(item.id, item.is_active)}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                        item.is_active
                          ? 'border-green-500/40 text-green-400 hover:bg-green-500/10'
                          : 'border-gray-600 text-gray-400 hover:bg-white/5'
                      }`}
                    >
                      {item.is_active ? 'فعال' : 'غیرفعال'}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="حذف"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* لاگ‌های ارزیابی و بررسی سردبیر */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h2 className="text-lg font-bold text-white mb-4">آخرین لاگ‌های نظارت و انتشار سردبیر (Editorial QA)</h2>
          {logs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">هنوز لاگی ثبت نشده است (پس از اولین تولید مقاله نمایش داده خواهد شد).</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-right text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-gray-400">
                    <th className="py-2 px-3">موضوع</th>
                    <th className="py-2 px-3">وضعیت سردبیر</th>
                    <th className="py-2 px-3">نمره کیفی</th>
                    <th className="py-2 px-3">تعداد بازنویسی</th>
                    <th className="py-2 px-3">تاریخ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td className="py-3 px-3 font-medium text-white">{log.topic}</td>
                      <td className="py-3 px-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] ${
                          log.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {log.status === 'approved' ? 'تایید و منتشر شد' : log.status}
                        </span>
                      </td>
                      <td className="py-3 px-3 font-bold text-primary">{log.score || 85}/100</td>
                      <td className="py-3 px-3 text-gray-300">{log.attempt_count}</td>
                      <td className="py-3 px-3 text-gray-400">{new Date(log.created_at).toLocaleDateString('fa-IR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
